import React from 'react';
import {
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { POSITION_OPTIONS, UNIT_OPTIONS } from '../constants/selectConstants';

export const LayoutSettings = ({ 
  width, maxWidth, minWidth, position, unit, lockedEdges,
  top, left, right, bottom, flex, flexGrow, flexShrink, flexBasis, setProp 
}) => {
  const handleFlexChange = (prop, value) => {
    if (value === '') {
      setProp(props => props[prop] = undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setProp(props => props[prop] = numValue);
      }
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="subtitle2">Layout & Positioning</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormControl fullWidth margin="normal">
          <FormLabel>Width</FormLabel>
          <TextField
            value={width || ''}
            onChange={(e) => setProp(props => props.width = e.target.value)}
            size="small"
            placeholder="100%, 300px, auto, or leave empty"
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Max Width</FormLabel>
          <TextField
            value={maxWidth || ''}
            onChange={(e) => setProp(props => props.maxWidth = e.target.value)}
            size="small"
            placeholder="400px, 50%, or leave empty"
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Min Width</FormLabel>
          <TextField
            value={minWidth || ''}
            onChange={(e) => setProp(props => props.minWidth = e.target.value)}
            size="small"
            placeholder="100px, 20%, or leave empty"
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Position</FormLabel>
          <Select
            value={position || 'relative'}
            onChange={(e) => setProp(props => props.position = e.target.value)}
            size="small"
          >
            {POSITION_OPTIONS.map(pos => (
              <MenuItem key={pos.value} value={pos.value}>{pos.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(position === 'absolute' || position === 'relative') && (
          <>
            <FormControl fullWidth margin="normal">
              <FormLabel>Unit</FormLabel>
              <ToggleButtonGroup
                value={unit}
                exclusive
                onChange={(e, value) => value && setProp(props => props.unit = value)}
                size="small"
              >
                {UNIT_OPTIONS.map(u => (
                  <ToggleButton key={u} value={u}>{u}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel>Position Controls</FormLabel>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lockedEdges?.top || false}
                      onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, top: e.target.checked })}
                    />
                  }
                  label="Top"
                />
                <TextField
                  value={top || ''}
                  onChange={(e) => setProp(props => props.top = parseFloat(e.target.value) || 0)}
                  size="small"
                  type="number"
                  disabled={!lockedEdges?.top}
                  style={{ width: '80px' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lockedEdges?.left || false}
                      onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, left: e.target.checked })}
                    />
                  }
                  label="Left"
                />
                <TextField
                  value={left || ''}
                  onChange={(e) => setProp(props => props.left = parseFloat(e.target.value) || 0)}
                  size="small"
                  type="number"
                  disabled={!lockedEdges?.left}
                  style={{ width: '80px' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lockedEdges?.right || false}
                      onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, right: e.target.checked })}
                    />
                  }
                  label="Right"
                />
                <TextField
                  value={right || ''}
                  onChange={(e) => setProp(props => props.right = parseFloat(e.target.value) || 0)}
                  size="small"
                  type="number"
                  disabled={!lockedEdges?.right}
                  style={{ width: '80px' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={lockedEdges?.bottom || false}
                      onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, bottom: e.target.checked })}
                    />
                  }
                  label="Bottom"
                />
                <TextField
                  value={bottom || ''}
                  onChange={(e) => setProp(props => props.bottom = parseFloat(e.target.value) || 0)}
                  size="small"
                  type="number"
                  disabled={!lockedEdges?.bottom}
                  style={{ width: '80px' }}
                />
              </div>
            </FormControl>
          </>
        )}

        <FormControl fullWidth margin="normal">
          <FormLabel>Flex</FormLabel>
          <TextField
            value={flex || ''}
            onChange={(e) => handleFlexChange('flex', e.target.value)}
            size="small"
            placeholder="1, 0 1 auto, or leave empty"
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Flex Grow</FormLabel>
          <TextField
            value={flexGrow || ''}
            onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
            size="small"
            placeholder="0, 1, 2, or leave empty"
            type="number"
            inputProps={{ min: 0, step: 1 }}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Flex Shrink</FormLabel>
          <TextField
            value={flexShrink || ''}
            onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
            size="small"
            placeholder="1, 0, or leave empty"
            type="number"
            inputProps={{ min: 0, step: 1 }}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Flex Basis</FormLabel>
          <TextField
            value={flexBasis || ''}
            onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
            size="small"
            placeholder="auto, 200px, 50%, or leave empty"
          />
        </FormControl>
      </AccordionDetails>
    </Accordion>
  );
};